import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { closeModal, showToastMessage } from 'enable-ui';
import { useFormik } from 'formik';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { MODAL_KEY } from 'modules/auth/core/modal-key';

import type { Assessment, Courses, Lessons } from '../type';

import { hasEmptyValues, reorder, trimObject, trimValue } from 'helpers';
import { useBoolean, useNumber, useString } from 'hooks';
import { apiCall } from 'hooks/service';
import { cloneDeep, debounce, isEmpty, isUndefined, orderBy } from 'lodash';

import { SUCCESS_MESSAGE } from 'constants/message';
import { PATH } from 'constants/path';

import { defaultCourses } from '../constant';

export const useCoursesFormController = () => {
  const navigationTo = useNavigate();

  const params = useParams();

  const { id, institutionId } = params;

  const userProfile = useAuthStore(userProfileSelector);

  const isInstitutionDetail = location.pathname.includes(`${PATH.INSTITUTIONS}`);

  const isCourseAdd = location.pathname.includes(`${PATH.ADD_COURSES}`);

  const [selectedLessons, setSelectedLessons] = useState<Record<string, string>>({});

  const [lessonsData, setLessonsData] = useState<Lessons[]>([]);

  const searchValue = useString('');

  const [selectedData, setSelectedData] = useState<Lessons[]>([]);

  const [deleteSelected, setDeleteSelected] = useState<Lessons[]>([]);

  const [courseDetail, setCourseDetail] = useState<Courses>();

  const [assessmentData, setAssessmentData] = useState<Assessment[]>([]);

  const [selectedAssessments, setSelectedAssessments] = useState<Record<string, string>>({});

  const searchAssessment = useString('');

  const [selectedAssessmentData, setSelectedAssessmentData] = useState<Assessment[]>([]);

  const [deleteAssessmentSelected, setDeleteAssessmentSelected] = useState<Assessment[]>([]);

  const [pageLesson, setPageLesson] = useState<number>(1);

  const [pageAssessment, setPageAssessment] = useState<number>(1);

  const hasMoreLesson = useBoolean(false);

  const hasMoreAssessment = useBoolean(false);

  const totalPageLesson = useNumber(0);

  const totalPageAssessment = useNumber(0);

  const isLoading = useBoolean(false);

  useEffect(() => {
    if (pageLesson < 1) {
      return;
    }

    getListLessons();
  }, [pageLesson]);

  useEffect(() => {
    if (pageAssessment < 1) {
      return;
    }

    getListAssessments();
  }, [pageAssessment]);

  useEffect(() => {
    if (isCourseAdd) {
      return;
    }

    if (id) {
      getCourseDetail();
    }
  }, [id]);

  const getCourseDetail = async () => {
    const result: Courses = await apiCall({
      url: `/courses/${id}`,
      method: 'GET',
      isLoading: true,
    });

    if (result?.id) {
      setValues(result as any);
      setCourseDetail({
        ...result,
        lessons: orderBy(result.lessons, 'index'),
        assessments: orderBy(result.assessments, 'index'),
      });

      setSelectedData(orderBy(result.lessons, 'index'));

      setSelectedAssessmentData(orderBy(result.assessments, 'index'));
    }
  };

  const getListLessons = async (search: string = '') => {
    const result: {
      items: Lessons[];
      total: number;
    } = await apiCall({
      url: '/lessons',
      method: 'GET',
      data: {
        page: pageLesson.toString(),
        limit: 10,
        institutionId: isInstitutionDetail ? institutionId : userProfile?.institution?.id,
        search: search,
      },
    });

    hasMoreLesson.setValue(result?.items?.length === 10);

    setTimeout(() => {
      isLoading.setValue(false);
    }, 1000);

    const newItems = result?.items ?? [];

    if (pageLesson < 1) {
      return;
    }

    if (pageLesson === 1) {
      setLessonsData(newItems);
      totalPageLesson.setValue(result.total);

      return;
    }

    setLessonsData((prev) => [...prev, ...newItems]);
  };

  const getListAssessments = async (search: string = '') => {
    const result: {
      items: Assessment[];
      total: number;
    } = await apiCall({
      url: '/assessments',
      method: 'GET',
      data: {
        page: pageAssessment.toString(),
        limit: 10,
        institutionId: isInstitutionDetail ? institutionId : userProfile?.institution?.id,
        search: search,
      },
    });

    hasMoreAssessment.setValue(result?.items?.length === 10);

    setTimeout(() => {
      isLoading.setValue(false);
    }, 1000);

    const newItems = result?.items ? result.items.filter((el) => el.isClass) : [];

    if (pageAssessment < 1) {
      return;
    }

    if (pageAssessment === 1) {
      setAssessmentData(newItems);
      totalPageAssessment.setValue(result.total);

      return;
    }

    setAssessmentData((prev) => [...prev, ...newItems]);
  };

  const onChangeSelectedLessons = (lesson: string) => () => {
    const newSelected = cloneDeep(selectedLessons);

    if (!isUndefined(newSelected[lesson])) {
      delete newSelected[lesson];
    } else {
      newSelected[lesson] = lesson;
    }

    setSelectedLessons(newSelected);
  };

  const formik = useFormik({
    initialValues: defaultCourses,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      if (selectedAssessmentData.length > 3) {
        showToastMessage('error', 'You can only add up to 3 assessments');

        return;
      }

      const bodyRequest = {
        ...trimObject({
          name: values.name,
          institutionId: userProfile?.institution?.id,
          lessons: selectedData.map((el, index) => ({
            index,
            lessonId: el.id,
            courseLessonId: el.courseLessonId,
          })),
          removeCourseLessons: deleteSelected.map((el) => el.courseLessonId),
          assessments: selectedAssessmentData.map((el, index) => ({
            index,
            assessmentId: el.id,
            courseAssessmentId: el.courseAssessmentId,
          })),
          removeCourseAssessments: deleteAssessmentSelected.map((el) => el.courseAssessmentId),
        }),
      };

      if (isCourseAdd && isInstitutionDetail) {
        bodyRequest.institutionId = institutionId;
      }

      const result: Courses = await apiCall({
        url: !isCourseAdd ? `/courses/${id}` : '/courses',
        method: !isCourseAdd ? 'PATCH' : 'POST',
        data: bodyRequest,
        isLoading: true,
        showError: true,
      });

      if (!result?.id) {
        return;
      }

      if (!isCourseAdd) {
        getCourseDetail();
        showToastMessage('success', SUCCESS_MESSAGE.UPDATE_COURSE);

        return;
      }

      formik.setValues(defaultCourses);
      setSelectedLessons({});
      showToastMessage('success', SUCCESS_MESSAGE.ADD_COURSE);

      navigationTo(-1);
    },
  });

  const { values, isSubmitting, isValid, handleSubmit, handleChange, setValues } = formik;

  const disabled =
    hasEmptyValues({ name: values.name }) ||
    isSubmitting ||
    !isValid ||
    isEmpty(selectedData && selectedAssessmentData);

  const onChangeSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchValue.setValue(event.target.value);
    debounceSearchValue(event.target.value);
  };

  const debounceSearchValue = useCallback(
    debounce((newSearch: string) => {
      getListLessons(trimValue(newSearch));
    }, 500),
    [],
  );

  const handleAddLessonsToCourse = () => {
    const newSelectedData = lessonsData.filter((el) =>
      Object.keys(selectedLessons).includes(el.id),
    );

    setSelectedData([...selectedData, ...newSelectedData]);
    setSelectedLessons({});
  };

  const handleDeleteCourse = async () => {
    await apiCall({
      url: `/courses/${id}`,
      method: 'DELETE',
      isLoading: true,
      showError: true,
    });
    closeModal(MODAL_KEY.DELETE_COURSE);
    showToastMessage('success', SUCCESS_MESSAGE.DELETE_COURSE);
    navigationTo(-1);
  };

  const onCancel = () => {
    if (!isCourseAdd && id) {
      setValues(courseDetail as any);
      setSelectedData(courseDetail?.lessons as Lessons[]);

      setSelectedAssessmentData(courseDetail?.assessments as Assessment[]);

      return;
    }

    formik.setValues(defaultCourses);
    navigationTo(-1);
  };

  const handleDeleteLessonInCourse = (index: number) => {
    const newSelectedData = cloneDeep(selectedData);

    setDeleteSelected([...deleteSelected, newSelectedData[index]]);

    newSelectedData.splice(index, 1);

    setSelectedData(newSelectedData);
  };

  const disabledButton = useMemo(() => {
    return (
      (String(courseDetail?.name) === values.name &&
        JSON.stringify(courseDetail?.lessons) === JSON.stringify(selectedData) &&
        JSON.stringify(courseDetail?.assessments) === JSON.stringify(selectedAssessmentData)) ||
      hasEmptyValues({ name: values.name })
    );
  }, [values, courseDetail, selectedData, selectedAssessmentData]);

  const goBack = () => {
    navigationTo(-1);
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const items = reorder(selectedData, result.source.index, result.destination.index);

    setSelectedData(items);
  };

  const onChangeSelectedAssessments = (assessment: string) => () => {
    const newSelected = cloneDeep(selectedAssessments);

    if (!isUndefined(newSelected[assessment])) {
      delete newSelected[assessment];
    } else {
      newSelected[assessment] = assessment;
    }

    setSelectedAssessments(newSelected);
  };

  const onChangeSearchAssessment = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchAssessment.setValue(event.target.value);
    debounceSearchAssessment(event.target.value);
  };

  const debounceSearchAssessment = useCallback(
    debounce((newSearch: string) => {
      getListAssessments(trimValue(newSearch));
    }, 500),
    [],
  );

  const handleAddAssessmentsToCourse = () => {
    const newSelectedData = assessmentData.filter((el) =>
      Object.keys(selectedAssessments).includes(el.id),
    );

    setSelectedAssessmentData([...selectedAssessmentData, ...newSelectedData]);
    setSelectedAssessments({});
  };

  const handleDeleteAssessmentInCourse = (index: number) => {
    const newSelectedData = cloneDeep(selectedAssessmentData);

    setDeleteAssessmentSelected([...deleteAssessmentSelected, newSelectedData[index]]);

    newSelectedData.splice(index, 1);

    setSelectedAssessmentData(newSelectedData);
  };

  const onDragEndAssessment = (result: any) => {
    if (!result.destination) {
      return;
    }

    const items = reorder(selectedAssessmentData, result.source.index, result.destination.index);

    setSelectedAssessmentData(items);
  };

  const handleLoadMoreLesson = () => {
    if (!hasMoreLesson.value || isLoading.value) {
      return;
    }

    isLoading.setValue(true);

    setTimeout(() => {
      setPageLesson((prev) => prev + 1);
    }, 1200);
  };

  const handleLoadMoreAssessment = () => {
    if (!hasMoreAssessment.value || isLoading.value) {
      return;
    }

    isLoading.setValue(true);

    setTimeout(() => {
      setPageAssessment((prev) => prev + 1);
    }, 1200);
  };

  return {
    lessonsData,
    selectedLessons,
    onChangeSelectedLessons,
    handleChange,
    handleSubmit,
    disabled,
    values,
    courseId: isCourseAdd ? '' : id,
    searchValue,
    onChangeSearchValue,
    handleAddLessonsToCourse,
    selectedData,
    handleDeleteCourse,
    onCancel,
    disabledButton,
    handleDeleteLessonInCourse,
    courseDetail,
    goBack,
    onDragEnd,
    assessmentData,
    onChangeSelectedAssessments,
    selectedAssessments,
    onChangeSearchAssessment,
    searchAssessment,
    handleAddAssessmentsToCourse,
    selectedAssessmentData,
    handleDeleteAssessmentInCourse,
    onDragEndAssessment,
    hasMoreLesson,
    handleLoadMoreLesson,
    hasMoreAssessment,
    handleLoadMoreAssessment,
  };
};
